import { PaletteOptions } from '@mui/material/styles';

// Shared brand colors
const brandColors = {
  primary: {
    main: '#1E40AF',
    light: '#3B82F6',
    dark: '#1E3A8A',
    contrastText: '#FFFFFF',
  },
  secondary: {
    main: '#0EA5E9',
    light: '#38BDF8',
    dark: '#0284C7',
    contrastText: '#FFFFFF',
  },
  accent1: {
    main: '#10B981',
    light: '#34D399',
    dark: '#059669',
    contrastText: '#FFFFFF',
  },
  accent2: {
    main: '#F59E0B',
    light: '#FBBF24',
    dark: '#D97706',
    contrastText: '#000000',
  },
  accent3: {
    main: '#0F172A',
    light: '#1E293B',
    dark: '#020617',
    contrastText: '#FFFFFF',
  },
  blue: {
    main: '#3B82F6',
    light: '#60A5FA',
    dark: '#2563EB',
    contrastText: '#FFFFFF',
  },
  navy: {
    main: '#1E3A8A',
    light: '#1E40AF',
    dark: '#172554',
    contrastText: '#FFFFFF',
  },
  success: {
    main: '#10B981',
    light: '#34D399',
    dark: '#059669',
    contrastText: '#FFFFFF',
  },
  error: {
    main: '#EF4444',
    light: '#F87171',
    dark: '#DC2626',
    contrastText: '#FFFFFF',
  },
  warning: {
    main: '#F59E0B',
    light: '#FBBF24',
    dark: '#D97706',
    contrastText: '#000000',
  },
  info: {
    main: '#0EA5E9',
    light: '#38BDF8',
    dark: '#0284C7',
    contrastText: '#FFFFFF',
  },
  chart: {
    blue: '#3B82F6',
    cyan: '#06B6D4',
    emerald: '#10B981',
    amber: '#F59E0B',
    rose: '#F43F5E',
    violet: '#8B5CF6',
  },
};

// Dark mode palette
export const darkPalette = {
  ...brandColors,
  mode: 'dark' as const,
  background: {
    default: '#0B1120',
    paper: '#111827',
    glass: 'rgba(17, 24, 39, 0.85)',
    elevated: '#1F2937',
    gradient: {
      primary: 'linear-gradient(135deg, rgba(30, 64, 175, 0.15) 0%, rgba(14, 165, 233, 0.15) 100%)',
      secondary: 'linear-gradient(135deg, rgba(30, 64, 175, 0.1) 0%, rgba(16, 185, 129, 0.1) 100%)',
      accent: 'linear-gradient(135deg, rgba(30, 64, 175, 0.2) 0%, rgba(14, 165, 233, 0.2) 100%)',
      hero: 'linear-gradient(135deg, rgba(30, 64, 175, 0.25) 0%, rgba(14, 165, 233, 0.15) 50%, rgba(16, 185, 129, 0.1) 100%)',
      card: 'linear-gradient(145deg, rgba(17, 24, 39, 0.95) 0%, rgba(31, 41, 55, 0.95) 100%)',
    },
  },
  text: {
    primary: '#F9FAFB',
    secondary: '#9CA3AF',
    disabled: '#6B7280',
    blue: '#60A5FA',
    navy: '#93C5FD',
    teal: '#22D3EE',
    green: '#34D399',
    amber: '#FBBF24',
  },
  border: {
    light: 'rgba(255, 255, 255, 0.1)',
    medium: 'rgba(255, 255, 255, 0.15)',
    focus: 'rgba(59, 130, 246, 0.5)',
  },
  divider: 'rgba(255, 255, 255, 0.08)',
};

// Light mode palette
export const lightPalette = {
  ...brandColors,
  mode: 'light' as const,
  background: {
    default: '#F8FAFC',
    paper: '#FFFFFF',
    glass: 'rgba(255, 255, 255, 0.9)',
    elevated: '#F1F5F9',
    gradient: {
      primary: 'linear-gradient(135deg, rgba(30, 64, 175, 0.08) 0%, rgba(14, 165, 233, 0.08) 100%)',
      secondary: 'linear-gradient(135deg, rgba(30, 64, 175, 0.05) 0%, rgba(16, 185, 129, 0.05) 100%)',
      accent: 'linear-gradient(135deg, rgba(30, 64, 175, 0.1) 0%, rgba(14, 165, 233, 0.1) 100%)',
      hero: 'linear-gradient(135deg, rgba(30, 64, 175, 0.1) 0%, rgba(14, 165, 233, 0.08) 50%, rgba(16, 185, 129, 0.05) 100%)',
      card: 'linear-gradient(145deg, rgba(255, 255, 255, 0.95) 0%, rgba(248, 250, 252, 0.95) 100%)',
    },
  },
  text: {
    primary: '#0F172A',
    secondary: '#475569',
    disabled: '#94A3B8',
    blue: '#2563EB',
    navy: '#1E3A8A',
    teal: '#0891B2',
    green: '#059669',
    amber: '#D97706',
  },
  border: {
    light: 'rgba(0, 0, 0, 0.08)',
    medium: 'rgba(0, 0, 0, 0.12)',
    focus: 'rgba(59, 130, 246, 0.5)',
  },
  divider: 'rgba(0, 0, 0, 0.08)',
};

// Type for the palette
export type ThemePalette = typeof darkPalette | typeof lightPalette;

// Get design tokens based on mode
export const getDesignTokens = (mode: 'light' | 'dark'): { palette: PaletteOptions & ThemePalette } => {
  const palette = mode === 'dark' ? darkPalette : lightPalette;

  return {
    palette: {
      ...palette,
      mode,
    } as PaletteOptions & ThemePalette,
  };
};

// Legacy export for backward compatibility
export const palette = darkPalette;
